import { RadioFieldUI } from '@axo/form/feature/form-input/RadioField/RadioFieldUI';
import { loan_application } from '@axo/shared/data-access/types';
import { useTranslation } from '@axo/shared/i18n';
import { DataSharingStatus } from '@axo/shared/types';
import { Button } from '@axo/ui-core/components/Button';
import { Stack } from '@axo/ui-core/components/layout/item';
import { Message } from '@axo/ui-core/components/Message';
import { useCallback, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Form } from '../../../../../components/Form';
import { Navigation } from '../../../../../components/Navigation';
import { useAxoFormContext } from '../../../../../context/AxoForm/AxoForm';
import useInitiatedTracking from '../../../../../hooks/tracking/useInitiatedTracking';
import { getMarketingConsentDefaultValues } from '../../../../../hooks/useMarketingConsent';
import { useESkat } from '../../../ESkat';
import { radioOptions } from '../../config/globals';
import { debtIsFilled } from '../../config/utils/debtIsFilled';
import type { DenmarkFormData, Step7Data } from '../../FormData.dk.types';
import { FallbackPurposeFormGroup } from '../../shared/FallbackPurposeFormGroup';
import { requiredField } from '../shared-config';
import { ConsentFormGroup } from './ConsentFormGroup';
import { DebtFormGroup } from './DebtFormGroup';
import { ESkatDebtFormGroup } from './ESkatDebtFormGroup';

export function Step7() {
  const { t } = useTranslation();

  const { logged: isESkat } = useESkat();
  const { onChange, onSubmit, axoFormData } =
    useAxoFormContext<DenmarkFormData>();
  // TODO: ESkat login is not working on Step 7 DK
  const dataSharingStatus = DataSharingStatus.Disabled;

  const [askConsentModal, setAskConsentModal] = useState(false);
  const [formFlow, setFormFlow] = useState(() => {
    if (isESkat) return true;
    if (dataSharingStatus === DataSharingStatus.Disabled) return true;

    if (
      axoFormData['LoanApplication/Debts'] &&
      axoFormData['LoanApplication/Debts'].length > 0
    )
      return true;

    return !!axoFormData['LoanApplication/PaymentRemarks'];
  });

  const form = useForm({
    defaultValues: {
      ...getMarketingConsentDefaultValues({
        marketingConsent: axoFormData['Ignore/MarketingConsent'],
        showMarketingConsent: axoFormData['Ignore/ShowMarketingConsent'],
        defaultValueOverride: false,
      }),
      'LoanApplication/Debts': axoFormData['LoanApplication/Debts'] || [
        {
          Creditor: null,
          OutstandingAmount: null,
          MonthlyDebtPayment: null,
          Debtor: loan_application.Debtor.MainApplicant,
        } as unknown as loan_application.Debt,
      ],
      'LoanApplication/CreditStatusConsent':
        axoFormData['LoanApplication/CreditStatusConsent'],
    },
    mode: 'onChange',
  });

  const {
    formState: { touchedFields, isSubmitting },
    trigger,
  } = form;

  useInitiatedTracking({
    shouldTriggerOnInitiated: Object.keys(touchedFields).length > 0,
  });

  const _onSubmit = useCallback(
    async (data: Step7Data) => {
      const missingFieldDebt = data['LoanApplication/Debts']?.some(
        (debt) => !debtIsFilled(debt)
      );

      if (missingFieldDebt) {
        trigger('LoanApplication/Debts');
        return;
      }

      if (
        !data['LoanApplication/CreditStatusConsent'] &&
        !data['Frontend/CreditStatusConsentRejected']
      ) {
        setAskConsentModal(true);
        return;
      }

      void onSubmit(data);
    },
    [onSubmit, trigger]
  );

  const onConsentSelect = () => {
    setAskConsentModal(false);
    _onSubmit(form.getValues());
  };

  const onCloseModal = () => {
    setAskConsentModal(false);
  };

  const showMoreFields = isESkat || formFlow;

  return (
    <FormProvider {...form}>
      <Form
        onSubmit={_onSubmit}
        onChange={onChange}
        touchedFieldsOverride={['LoanApplication/Debts']}
      >
        <Stack gap="xs">
          <ESkatDebtFormGroup />

          {!showMoreFields && (
            <>
              <Button
                variant="secondary"
                onClick={() => setFormFlow(true)}
                data-testid="add-manually-button"
              >
                {t('ESkat.manual_input')}
              </Button>

              <Message hasIcon variant="warn">
                {t('Debts.warning')}
              </Message>
            </>
          )}

          {showMoreFields && (
            <>
              <DebtFormGroup />

              <RadioFieldUI<Step7Data>
                name="LoanApplication/PaymentRemarks"
                label={t('PaymentRemarks.label')}
                data-testid="has-payment-remarks-radio"
                radioStyle="both"
                options={radioOptions}
                registerOptions={requiredField}
              />

              <FallbackPurposeFormGroup />

              <ConsentFormGroup
                isModalOpen={askConsentModal}
                onClick={onConsentSelect}
                onCloseModal={onCloseModal}
              />

              <Navigation.Continue loading={isSubmitting}>
                {t('next')}
              </Navigation.Continue>
            </>
          )}
        </Stack>
      </Form>
    </FormProvider>
  );
}
